import {
  slab, wallZ, wallX, sign, neon, checkpoint, edgeLights,
  ring, bouncefloor, gravfield, wind, speedgate, pad,
} from '../levels/kit.js';

/**
 * THE GAUNTLET: a daily-seeded tower of chunks, one life, depth as the
 * score. Every chunk template carries the NUMBERS of a shipped, suite-
 * verified sector: the tower is remixed proof, not fresh invention. All
 * chunks flow down-course (-Z, descending), entry decks are a standard
 * 28x28, and each template's flight was tuned to land on the next one.
 */

/** Deterministic PRNG (mulberry32); Math.random would break the daily. */
function rng(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const deck = (y, z, extra = []) => [
  slab(0, y, z, 28, 28, 'plate', 1.4),
  ...edgeLights(0, y, z, 28, 28, 'cyan'),
  ...extra,
];

/**
 * Each template: (y, z) anchor = its entry deck centre. Returns objects
 * (INCLUDING the entry deck), the exit anchor (the next chunk's entry deck
 * position, where this chunk's crossing lands), the lowest solid it owns
 * (for the death plane), and whether a zero-skill walker survives it.
 */
const TEMPLATES = {
  // CASCADE numbers: ring at deck height just past the edge.
  ringHop: (y, z) => ({
    objects: [
      ...deck(y, z),
      ring(0, y + 0.4, z - 17, [0, 0.42, -0.91], 22, 3),
    ],
    exit: { y: y - 8, z: z - 48 },
    floorMin: y - 8,
    walker: true,
  }),
  // ZEPHYR numbers: a jetstream over a descending chasm.
  jetstream: (y, z) => ({
    objects: [
      ...deck(y, z),
      gravfield(0, y - 14, z - 27, 22, 24, 30),
      wind(0, y - 14, z - 27, 20, 24, 28, [0, 0, -1], 18, 26),
    ],
    exit: { y: y - 10, z: z - 52 },
    floorMin: y - 14,
    walker: true,
  }),
  // REBOUND numbers: a kinetic floor returns the fall onto the next deck.
  bounceDrop: (y, z) => ({
    objects: [
      ...deck(y, z),
      bouncefloor(0, y - 20, z - 26, 16, 18),
      neon(0, y - 19.2, z - 26, [14, 0.14, 0.14], 'lime'),
    ],
    // Leave room to rise before the next deck: a late hopping entry
    // otherwise bounces into its underside and loses the return impulse.
    exit: { y: y - 8, z: z - 46 },
    floorMin: y - 20,
    walker: true,
  }),
  // VELOCITY numbers: the pad alone lands you safely on the far deck.
  padLaunch: (y, z) => ({
    objects: [
      ...deck(y, z),
      pad(0, y, z - 8, 42, [0, 0.5, -0.87]),
    ],
    exit: { y: y - 4, z: z - 84 },
    floorMin: y - 4,
    walker: true,
  }),
  // SLIPSTREAM ch1 numbers: the jetstream pays the gate's toll mid-air.
  gateStream: (y, z) => ({
    objects: [
      ...deck(y, z),
      gravfield(0, y - 14, z - 47, 22, 24, 74),
      wind(0, y - 14, z - 47, 20, 24, 72, [0, 0, -1], 20, 34),
      speedgate(0, y - 2, z - 62, 26, 18, 30),
    ],
    exit: { y: y - 12, z: z - 96 },
    floorMin: y - 14,
    // NOT walker-passable: the gate repels anything under 30 m/s, and a
    // hold-forward walker tops out at walk speed (TURBINE's own suite
    // assertion is literally "speed gate repels a walker"). Claiming
    // walker: true seated this in a protected first-two slot on seed
    // g20260819 and stalled the daily tower's on-ramp at the gate.
    walker: false,
  }),
  // CONFLUX ch2 / FREEFALL numbers: the pit takes a portal, the wall on the
  // deck faces the course. The tower's skill check.
  pitFling: (y, z) => ({
    objects: [
      ...deck(y, z, [
        sign(0, y + 3.2, z + 10, 0, ['PORTAL THE PIT', 'THE WALL FACES THE COURSE'], '#ffb020'),
      ]),
      wallZ(0, y - 46, z - 13.25, 20, 46, 'plate', 1.5),
      wallZ(0, y - 46, z - 37.75, 20, 34, 'plate', 1.5),
      wallX(-10.25, y - 46, z - 25, 24, 46, 'plate', 1.5),
      wallX(10.25, y - 46, z - 25, 24, 46, 'plate', 1.5),
      slab(0, y - 46, z - 24.5, 18, 24, 'panel', 1.5),
      neon(0, y - 45.4, z - 24.5, [16, 0.14, 0.14], 'magenta'),
      wallZ(0, y, z - 8, 15, 12, 'panel', 1.4),
      neon(0, y + 12.4, z - 8.7, [15, 0.14, 0.14], 'cyan'),
    ],
    exit: { y: y - 50, z: z - 110 },
    floorMin: y - 46,
    walker: false,
  }),
};

export const CHUNK_KEYS = Object.keys(TEMPLATES);

// The walker flag lives on the chunk a template RETURNS, not on the
// template function itself. The original filter read `.walker` off the
// function (always undefined, `?? true` passed everything), so the
// "first two chunks are walker-safe" contract was never enforced: seed
// g20260819 legally seated pitFling, a portal-required chunk, in slot 2.
export const WALKER_KEYS = CHUNK_KEYS.filter((k) => TEMPLATES[k](0, 0).walker ?? true);

/**
 * A one-chunk tower for tests: the per-template walker contract is proven
 * by walking each walker-flagged chunk in isolation, not by hoping the
 * daily seed happens to exercise it.
 */
export function singleChunkDef(key) {
  const chunk = TEMPLATES[key](0, 0);
  const objects = [checkpoint(0, 0, 6), ...chunk.objects, ...deck(chunk.exit.y, chunk.exit.z)];
  return {
    def: {
      id: 'gauntlet',
      name: `CHUNK ${key.toUpperCase()}`,
      blurb: 'test rig',
      mode: { type: 'tower' },
      spawn: { p: [0, 0.4, 8], yaw: 0 },
      killY: Math.min(0, chunk.exit.y, chunk.floorMin) - 200,
      env: { fog: 0x140b26, fogDensity: 0.004, floorY: -400 },
      objects,
      gauntlet: {
        planes: [-14, chunk.exit.z - 14],
        deathPlanes: [Math.min(0, chunk.exit.y, chunk.floorMin) - 55,
          chunk.exit.y - 55],
      },
    },
    exit: chunk.exit,
    walker: chunk.walker ?? true,
  };
}
export const GAUNTLET_DEPTH = 18;

/** The day's tower. Same UTC date, same tower, for everyone. */
export function generateGauntlet(date = new Date()) {
  const key = date.getUTCFullYear() * 10000 + (date.getUTCMonth() + 1) * 100 + date.getUTCDate();
  const next = rng(key ^ 0x5eed);

  const objects = [
    sign(0, 3.4, 10, 0, ['THE GAUNTLET', 'ONE LIFE. HOW DEEP?', 'THE TOWER CHANGES DAILY'], '#ff8a1f'),
    checkpoint(0, 0, 6),
  ];
  const planes = [];
  const deathPlanes = [];

  let y = 0;
  let z = 0;
  let prev = null;
  const picks = [];
  for (let i = 0; i < GAUNTLET_DEPTH; i++) {
    // The first two chunks never demand portals, and no template repeats
    // back to back; past depth eight the skill check gets more common.
    let pool = i < 2 ? WALKER_KEYS : CHUNK_KEYS;
    if (i >= 8) pool = pool.concat(['pitFling']);
    pool = pool.filter((k) => k !== prev);
    const pick = pool[Math.floor(next() * pool.length)];
    prev = pick;
    picks.push(pick);

    const chunk = TEMPLATES[pick](y, z);
    objects.push(...chunk.objects);
    objects.push(neon(-13, y + 0.4, z, [0.2, 0.2, 3], i % 4 === 0 ? 'magenta' : 'amber'));
    planes.push(z - 14);
    deathPlanes.push(Math.min(y, chunk.exit.y, chunk.floorMin) - 55);
    y = chunk.exit.y;
    z = chunk.exit.z;
  }
  // The final deck exists even though nothing follows it: depth 18 has to
  // have somewhere to stand.
  objects.push(...deck(y, z));
  planes.push(z - 14);
  deathPlanes.push(y - 55);

  return {
    board: `g${key}`,
    def: {
      id: 'gauntlet',
      name: 'THE GAUNTLET',
      blurb: 'One life. How deep?',
      mode: { type: 'tower' },
      spawn: { p: [0, 0.4, 8], yaw: 0 },
      killY: -100000,
      env: {
        fog: 0x1a0a1e,
        fogDensity: 0.0036,
        sky: { zenith: 0x0a0410, horizon: 0x2a0e2e, glow: 0xb04a18, aurora: 0xff8a1f },
        floorY: y - 220,
      },
      objects,
      gauntlet: { chunks: picks, planes, deathPlanes },
    },
  };
}
